import { Injectable } from "@angular/core";
import { map, take, switchMap, catchError } from 'rxjs/operators';
import { VacanteResumen } from "../Interfaces/vacantes/vacante-resumen.interface";
import { VacantesResultados } from "../Interfaces/vacantes/vacantes-resultado.interface";
import { delay, Observable, of, shareReplay, throwError } from "rxjs";
import { HttpClient, HttpParams, HttpHeaders } from "@angular/common/http";
import { FiltrosBusquedaVacante } from "../Interfaces/vacantes/filtros.interface";
import { Empresa } from "../Interfaces/vacantes/empresa.interface";
import { environment } from "src/environments/environment";
import { AuthApiService } from 'src/app/core/services/auth-api-service';
import { getFechasDesdeAntiguedad } from 'src/app/core/utils/date-utils';

@Injectable({ providedIn: "root" })
export class SearchJobApiService {
    user$ = this.authApiService.getUser();

    constructor(private httpClient: HttpClient, private authApiService: AuthApiService) { }

    getVacantesRequisicion(requisicion: number): Observable<VacantesResultados> {
        return this.httpClient
            .get<VacantesResultados>(`${environment.urlApiVacantes}/buscarVacantesRequisicion?requisicion=${requisicion}`)
            .pipe(shareReplay(1));
    }

    getVacantesPorFiltro(filtros: FiltrosBusquedaVacante): Observable<any> {
      return this.user$.pipe(
        take(1),
        map(user => {
          const params = new HttpParams({ fromObject: this.buildFiltrosParams(filtros, user) });
          return params;
        }),
        switchMap(params => {
          // Si useProxy es true y urlAppian es relativa, usar proxy, sino usar URL completa
          const url = (environment.useProxy && environment.urlAppian.startsWith('/')) 
            ? `${environment.urlAppian}/posiciones`
            : environment.urlAppianPosiciones;
          const headers = new HttpHeaders({
            'appian-api-key': environment.appianApiKey,
            'Accept': 'application/json'
          });
          
          // Construir URL completa con parámetros para mostrar en consola
          const paramsString = params.toString();
          const urlCompletaReal = paramsString 
            ? `${url}?${paramsString}` 
            : url;
          
          // Construir objeto con parámetros para mostrar
          const paramsObj: Record<string, string> = {};
          params.keys().forEach(key => {
            const value = params.get(key);
            if (value !== null) {
              paramsObj[key] = value;
            }
          });
          
          console.log('🔍 URL completa del servicio de búsqueda de vacantes:', urlCompletaReal);
          console.log('📋 Parámetros enviados:', paramsObj);
          console.log('🌐 URL base Appian:', url);
          console.log('🔑 useProxy:', environment.useProxy);
          console.log('🔑 urlAppian:', environment.urlAppian);
          console.log('🔑 Headers enviados:', headers.keys());
          
          return this.httpClient.get<any>(url, { params, headers }).pipe(
            map((response: any) => {
              console.log('📥 Respuesta completa de Appian posiciones:', response);
              console.log('📥 Tipo de respuesta:', typeof response);
              console.log('📥 Es array?:', Array.isArray(response));
              
              // Si la respuesta es un array, retornarla directamente
              if (Array.isArray(response)) {
                return response;
              }
              
              // Si la respuesta tiene una propiedad que contiene el array
              if (response && response.data && Array.isArray(response.data)) {
                return response.data;
              }
              
              // Si la respuesta tiene una propiedad hits o resultados
              if (response && response.hits && Array.isArray(response.hits)) {
                return response.hits;
              }
              
              // Si es un objeto único, convertirlo a array
              if (response && typeof response === 'object') {
                return [response];
              }
              
              // Si no hay datos, retornar array vacío
              console.warn('⚠️ Respuesta no reconocida, retornando array vacío');
              return [];
            }),
            catchError((error: any) => {
              console.error('❌ Error al obtener vacantes de Appian:', error);
              console.error('❌ Status:', error.status);
              console.error('❌ Mensaje:', error.message);
              console.error('❌ Error completo:', error);
              return throwError(() => error);
            })
          );
        }),
        shareReplay(1)
      );
    }
   
    getEmpresaPorId(empresaId: string): Observable<Empresa> {
        let empresaInfo: Empresa = {
            description: "Descripcion empresa " + empresaId,
            employeeRange: "500 - 1000",
            id: empresaId,
            name: "Nombre empresa ",
            type: "Tipo empresa",
            url: "https://media.licdn.com/dms/image/v2/D4E0BAQHcxMPC_Sa9Wg/company-logo_200_200/company-logo_200_200/0/1720447772398/manpowergroup_logo?e=2147483647&v=beta&t=QsHxk2ymeNUnaDeZFdpEUzUEOVwo7Kek2B9-TOoXr6A",

        }
        return of(empresaInfo).pipe(delay(2000));
    }

    generateRandomWord(length: number): string {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    getSalarioMinimoMaximo() {
      return (sueldoTexto: string | number | null | undefined): [number, number] => {
        // Validar que sueldoTexto sea un string válido
        if (!sueldoTexto) {
          return [0, 1000000];
        }
        
        // Convertir a string si es número
        const sueldoStr = typeof sueldoTexto === 'string' ? sueldoTexto : String(sueldoTexto);
        
        // Si no contiene guión, tratar como un solo valor
        if (!sueldoStr.includes('-')) {
          const sueldo = parseInt(sueldoStr.replace(/[^0-9]/g, ''), 10);
          return [sueldo || 0, sueldo || 1000000];
        }
        
        const [sueldoMinimoStr, sueldoMaximoStr] = sueldoStr
          .split('-')
          .map(s => s.replace(/[^0-9]/g, ''));
          
        const sueldoMinimo = parseInt(sueldoMinimoStr, 10) || 0;
        const sueldoMaximo = parseInt(sueldoMaximoStr || '1000000', 10) || 1000000;

        return [sueldoMinimo, sueldoMaximo];
      };
    }

    private buildFiltrosParams(filtros: FiltrosBusquedaVacante, user: any): Record<string, string> {
      const params: Record<string, any> = {};

      // Texto de búsqueda
      if (filtros.job || filtros.idPuesto) {
        params['text'] = (filtros.job ?? filtros.idPuesto)!;
      }

      // Ubicación
      if (filtros.idUbicacion) {
        params['entidad_federativa'] = filtros.idUbicacion;
      }

      // Salario
      const salarioRef = filtros.salario || user?.preferencias?.sueldo;
      if (salarioRef && (typeof salarioRef === 'string' || typeof salarioRef === 'number')) {
        const [sueldoMinimo, sueldoMaximo] = this.getSalarioMinimoMaximo()(salarioRef);
        if (sueldoMinimo > 0 || sueldoMaximo < 1000000) {
          params['rangoMin'] = sueldoMinimo.toString();
          params['rangoMax'] = sueldoMaximo.toString();
        }
      }

      // Antigüedad
      if (filtros.antiguedad) {
        const { fechaInicial, fechaFinal } = getFechasDesdeAntiguedad(filtros.antiguedad);
        params['fecha_inicial'] = fechaInicial;
        params['fecha_final'] = fechaFinal;
      }

      return params;
    }


}